<script lang="ts">
  import { onMount } from 'svelte'
  import { apiBaseUrl } from '$lib/api/base-url'
  import { uploadPortalDoc } from '$lib/public/portal-upload'

  let email = $state('')
  let phase = $state<'signin' | 'sent' | 'exchanging' | 'ready' | 'error'>('signin')
  let errorMsg = $state('')

  async function requestLink(): Promise<void> {
    try {
      const res = await fetch(`${apiBaseUrl()}/public/portal/request-link`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      // Always generic - a matching email gets a link; we never reveal which.
      phase = res.ok ? 'sent' : 'error'
      if (!res.ok) errorMsg = 'Something went wrong - please try again.'
    } catch {
      // Network failure - don't leave the button silently un-fed back.
      phase = 'error'
      errorMsg = 'Something went wrong - please try again.'
    }
  }

  async function exchange(token: string): Promise<void> {
    phase = 'exchanging'
    try {
      const res = await fetch(`${apiBaseUrl()}/public/portal/session`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      if (!res.ok) {
        phase = 'error'
        errorMsg = 'That link is invalid or has already been used. Request a new one below.'
        return
      }
      const { portalToken } = (await res.json()) as { portalToken: string }
      sessionStorage.setItem('portalToken', portalToken)
      // Strip the one-time token from the URL so it doesn't linger in history.
      history.replaceState(null, '', window.location.pathname)
      phase = 'ready'
    } catch {
      // Network failure mustn't strand the page on the "signing in…" spinner.
      phase = 'error'
      errorMsg = "We couldn't sign you in - please request a new link below."
    }
  }

  interface PortalDoc {
    id: number
    fileName: string
    contentType: string
    sizeBytes: number
    uploadedAt: string
  }
  interface PortalReq {
    id: number
    type: string
    status: string
    needsInfoMessage: string | null
    animalName: string
    documents: PortalDoc[]
    createdAt: string
  }
  let requests = $state<PortalReq[]>([])

  function authHeader(): Record<string, string> {
    const t = sessionStorage.getItem('portalToken')
    return t ? { authorization: `Bearer ${t}` } : {}
  }

  const LOAD_ERROR = "We couldn't load your requests - please refresh."

  async function loadRequests(): Promise<void> {
    try {
      const res = await fetch(`${apiBaseUrl()}/public/portal/requests`, { headers: authHeader() })
      if (res.status === 401) {
        sessionStorage.removeItem('portalToken')
        phase = 'signin'
        errorMsg = 'Your session expired. Request a new sign-in link.'
        return
      }
      if (!res.ok) {
        errorMsg = LOAD_ERROR
        return
      }
      requests = ((await res.json()) as { items: PortalReq[] }).items
      errorMsg = ''
    } catch {
      errorMsg = LOAD_ERROR
    }
  }

  async function download(requestId: number, docId: number): Promise<void> {
    try {
      const res = await fetch(
        `${apiBaseUrl()}/public/portal/requests/${requestId}/documents/${docId}/download`,
        { headers: authHeader() },
      )
      if (!res.ok) return
      const { url } = (await res.json()) as { url: string }
      window.open(url, '_blank', 'noopener')
    } catch {
      /* ignore - retryable */
    }
  }

  let uploadingId = $state<number | null>(null)

  async function onUpload(requestId: number, e: Event): Promise<void> {
    const input = e.currentTarget as HTMLInputElement
    const file = input.files?.[0]
    if (!file) return
    uploadingId = requestId
    errorMsg = ''
    try {
      await uploadPortalDoc(requestId, file)
      await loadRequests()
    } catch {
      errorMsg = 'Upload failed - please try again.'
    } finally {
      uploadingId = null
      // Reset so re-selecting the SAME file still fires onchange (retry after a
      // failure), and doesn't re-submit on the next unrelated interaction.
      input.value = ''
    }
  }

  let renewingId = $state<number | null>(null)
  let renewMsg = $state('')

  // Renewal reuses the exact upload sequence, but completes via /renew, which
  // flags the ESA doc for re-review without changing the APPROVED status.
  async function onRenew(requestId: number, e: Event): Promise<void> {
    const input = e.currentTarget as HTMLInputElement
    const file = input.files?.[0]
    if (!file) return
    renewingId = requestId
    errorMsg = ''
    renewMsg = ''
    try {
      await uploadPortalDoc(requestId, file, 'renew')
      renewMsg = 'Thanks - your updated letter is with our reviewers. Your approval stays active.'
      await loadRequests()
    } catch {
      errorMsg = 'Upload failed - please try again.'
    } finally {
      renewingId = null
      input.value = ''
    }
  }

  $effect(() => {
    if (phase === 'ready') void loadRequests()
  })

  onMount(() => {
    const t = new URLSearchParams(window.location.search).get('token')
    if (t) {
      void exchange(t)
    } else if (sessionStorage.getItem('portalToken')) {
      phase = 'ready'
    }
  })
</script>

<div class="card">
  <h1>Your RentReadyPet portal</h1>

  {#if phase === 'ready'}
    <p class="ok">You're signed in.</p>
    {#if errorMsg}<p class="err">{errorMsg}</p>{/if}
    {#if renewMsg}<p class="ok">{renewMsg}</p>{/if}
    {#if requests.length === 0}
      <p class="muted">No requests found for your account yet.</p>
    {:else}
      <ul class="reqs">
        {#each requests as r (r.id)}
          <li>
            <div class="req-head">
              <strong>{r.animalName}</strong>
              <span class="badge">{r.status}</span>
            </div>
            {#if r.needsInfoMessage}
              <p class="err">More information needed: {r.needsInfoMessage}</p>
            {/if}
            {#if r.documents.length > 0}
              <div class="docs">
                {#each r.documents as d (d.id)}
                  <button class="link" onclick={() => download(r.id, d.id)}>{d.fileName}</button>
                {/each}
              </div>
            {/if}
            {#if r.status === 'AWAITING_DOCS' || r.status === 'NEEDS_INFO'}
              <label class="upload">
                {r.documents.length > 0 ? 'Add another document' : 'Upload your document'} (PDF, PNG,
                or JPEG)
                <input
                  type="file"
                  accept="application/pdf,image/png,image/jpeg"
                  disabled={uploadingId === r.id}
                  onchange={(e) => onUpload(r.id, e)}
                />
              </label>
              {#if uploadingId === r.id}<p class="muted">Uploading…</p>{/if}
            {/if}
            {#if r.type === 'ESA' && r.status === 'APPROVED'}
              <label class="upload">
                Upload an updated provider letter (PDF, PNG, or JPEG)
                <input
                  type="file"
                  accept="application/pdf,image/png,image/jpeg"
                  disabled={renewingId === r.id}
                  onchange={(e) => onRenew(r.id, e)}
                />
              </label>
              {#if renewingId === r.id}<p class="muted">Uploading…</p>{/if}
            {/if}
          </li>
        {/each}
      </ul>
    {/if}
  {:else if phase === 'exchanging'}
    <p class="muted">Signing you in…</p>
  {:else if phase === 'sent'}
    <p class="ok">
      If that email matches a submission, we've sent a sign-in link. Check your inbox.
    </p>
  {:else}
    {#if phase === 'error'}<p class="err">{errorMsg}</p>{/if}
    <p class="muted">Enter the email you used on your application and we'll send a sign-in link.</p>
    <label>
      Email
      <input type="email" bind:value={email} placeholder="you@example.com" />
    </label>
    <button onclick={requestLink} disabled={!email}>Email me a link</button>
  {/if}
</div>

<style>
  .card {
    background: var(--surface-2);
    padding: 32px;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    max-width: 560px;
  }
  h1 {
    margin: 0 0 12px;
    font-size: 1.4rem;
    color: var(--ink-strong);
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 12px 0;
    font-size: 0.9rem;
    color: var(--ink);
  }
  input {
    padding: 8px 10px;
    border: 1px solid var(--line-strong);
    border-radius: 8px;
    font-size: 0.95rem;
  }
  button {
    padding: 8px 16px;
    background: var(--brand);
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
  }
  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .muted {
    color: var(--muted);
  }
  .ok {
    color: var(--ok-ink);
  }
  .err {
    color: var(--err-ink);
  }
  .reqs {
    list-style: none;
    padding: 0;
    margin: 12px 0 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .reqs li {
    border: 1px solid var(--line);
    border-radius: 8px;
    padding: 12px;
  }
  .req-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .badge {
    font-size: 0.75rem;
    background: var(--brand-tint);
    color: var(--link);
    padding: 2px 8px;
    border-radius: 999px;
  }
  .docs {
    margin-top: 6px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .link {
    background: none;
    border: none;
    color: var(--brand);
    cursor: pointer;
    text-decoration: underline;
    padding: 0;
    font-size: 0.9rem;
  }
  .upload {
    display: block;
    margin-top: 8px;
    font-size: 0.85rem;
    color: var(--ink);
  }
</style>
